/* Infinote header behaviour: mega menus (hover/focus/keyboard), preview crossfade, scroll state, mobile menu.
   No dependencies. Timing values mirror assets/infinote-header.css. */
(() => {
  if (customElements.get('infinote-header')) return;

  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 150;
  const DESKTOP = window.matchMedia('(min-width: 1200px)');

  class InfinoteHeader extends HTMLElement {
    connectedCallback() {
      this.triggers = [...this.querySelectorAll('[data-panel-trigger]')];
      this.panels = {};
      this.querySelectorAll('[data-panel]').forEach((p) => { this.panels[p.dataset.panel] = p; });
      this.current = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.pointerType = 'mouse';
      this.menu = this.querySelector('[data-mobile-menu]');
      this.menuBtn = this.querySelector('[data-menu-open]');

      this.bindDesktop();
      this.bindMobile();
      this.bindScroll();
      this.bindCount();
    }

    /* ---------- desktop mega menus ---------- */

    bindDesktop() {
      this.addEventListener('pointerdown', (e) => { this.pointerType = e.pointerType || 'mouse'; }, true);

      this.querySelectorAll('[data-panel-owner]').forEach((owner) => {
        const name = owner.dataset.panelOwner;
        owner.addEventListener('mouseenter', () => { if (DESKTOP.matches && this.pointerType !== 'touch') this.scheduleOpen(name); });
      });
      // Moving onto an item without a panel closes any open one.
      this.querySelectorAll('.inf-header__item:not([data-panel-owner]), .inf-header__brand, .inf-header__utilities').forEach((el) => {
        el.addEventListener('mouseenter', () => { if (DESKTOP.matches && this.pointerType !== 'touch') this.scheduleClose(); });
      });
      this.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
      this.addEventListener('mouseleave', () => { if (DESKTOP.matches && this.pointerType !== 'touch') this.scheduleClose(); });

      this.addEventListener('focusin', (e) => {
        if (!DESKTOP.matches) return;
        const trigger = e.target.closest('[data-panel-trigger]');
        const inPanel = e.target.closest('[data-panel]');
        if (trigger) { if (!this.noFocusOpen) this.openNow(trigger.dataset.panelTrigger); }
        else if (!inPanel) { this.closeNow(); }
      });
      this.addEventListener('focusout', (e) => {
        if (!DESKTOP.matches) return;
        if (e.relatedTarget && !this.contains(e.relatedTarget)) this.closeNow();
      });

      this.triggers.forEach((t) => {
        t.addEventListener('click', (e) => {
          // On touch, the first tap opens the menu; a second tap follows the link.
          if (DESKTOP.matches && this.pointerType === 'touch' && this.current !== t.dataset.panelTrigger) {
            e.preventDefault();
            this.openNow(t.dataset.panelTrigger);
          }
        });
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.openNow(t.dataset.panelTrigger);
            this.focusLink(t.dataset.panelTrigger, 0);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            this.moveTrigger(t, e.key === 'ArrowRight' ? 1 : -1, e);
          }
        });
      });

      Object.entries(this.panels).forEach(([name, panel]) => {
        panel.addEventListener('keydown', (e) => this.panelKeys(name, panel, e));
        panel.querySelectorAll('a[data-preview]').forEach((a) => {
          const show = () => this.setPreview(panel, a);
          a.addEventListener('mouseenter', show);
          a.addEventListener('focus', show);
        });
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.current) {
          const name = this.current;
          const hadFocus = this.panels[name] && this.panels[name].contains(document.activeElement);
          this.closeNow();
          if (hadFocus) {
            // Returning focus to the trigger must not reopen the menu.
            this.noFocusOpen = true;
            this.querySelector(`[data-panel-trigger="${name}"]`)?.focus();
            this.noFocusOpen = false;
          }
        }
      });
      DESKTOP.addEventListener('change', () => { this.closeNow(); this.closeMenu(true); });
    }

    scheduleOpen(name) {
      clearTimeout(this.closeTimer);
      clearTimeout(this.openTimer);
      if (this.current === name) return;
      this.openTimer = setTimeout(() => this.openNow(name), this.current ? 0 : OPEN_DELAY);
    }

    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeNow(), CLOSE_DELAY);
    }

    openNow(name) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!this.panels[name]) return;
      if (this.current === name) return;
      if (this.current) this.setState(this.current, false);
      this.current = name;
      this.setState(name, true);
      this.resetPreview(this.panels[name]);
    }

    closeNow() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!this.current) return;
      this.setState(this.current, false);
      this.current = null;
    }

    setState(name, open) {
      this.panels[name]?.classList.toggle('is-open', open);
      this.querySelector(`[data-panel-trigger="${name}"]`)?.setAttribute('aria-expanded', String(open));
    }

    panelLinks(name) { return [...this.panels[name].querySelectorAll('a')]; }

    focusLink(name, index) {
      const links = this.panelLinks(name);
      if (!links.length) return;
      const i = Math.max(0, Math.min(index, links.length - 1));
      // Wait a frame so the panel is visible and focusable.
      requestAnimationFrame(() => links[i].focus());
    }

    panelKeys(name, panel, e) {
      const links = this.panelLinks(name);
      const i = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); links[Math.min(i + 1, links.length - 1)]?.focus(); }
      else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (i <= 0) this.querySelector(`[data-panel-trigger="${name}"]`)?.focus();
        else links[i - 1].focus();
      } else if (e.key === 'Home') { e.preventDefault(); links[0]?.focus(); }
      else if (e.key === 'End') { e.preventDefault(); links[links.length - 1]?.focus(); }
    }

    moveTrigger(current, dir, e) {
      const items = [...this.querySelectorAll('.inf-header__list .inf-link')];
      const i = items.indexOf(current);
      if (i === -1) return; // Shop All sits apart; arrows only travel the primary group.
      const next = items[i + dir];
      if (next) { e.preventDefault(); next.focus(); }
    }

    /* ---------- preview crossfade (images load on demand) ---------- */

    resetPreview(panel) {
      const a = panel.querySelector('[data-preview-a]');
      const b = panel.querySelector('[data-preview-b]');
      const url = panel.dataset.defaultPreview;
      panel.querySelectorAll('a.is-previewing').forEach((l) => l.classList.remove('is-previewing'));
      if (!a || !url) return;
      if (a.getAttribute('src') !== url) a.src = url;
      a.classList.add('is-active');
      b?.classList.remove('is-active');
      panel._shown = url;
    }

    setPreview(panel, link) {
      const url = link.dataset.preview;
      panel.querySelectorAll('a.is-previewing').forEach((l) => l.classList.remove('is-previewing'));
      link.classList.add('is-previewing');
      if (!url || panel._shown === url) return;
      const a = panel.querySelector('[data-preview-a]');
      const b = panel.querySelector('[data-preview-b]');
      if (!a || !b) return;
      const next = a.classList.contains('is-active') ? b : a;
      const prev = next === a ? b : a;
      const token = (panel._token = (panel._token || 0) + 1);
      const swap = () => {
        if (panel._token !== token) return; // a newer hover superseded this one
        next.classList.add('is-active');
        prev.classList.remove('is-active');
        panel._shown = url;
      };
      next.onload = swap;
      next.onerror = () => { if (panel._token === token) panel._token = token + 1; };
      next.alt = '';
      if (next.getAttribute('src') === url && next.complete) swap();
      else next.src = url;
    }

    /* ---------- scroll state (subtle) ---------- */

    bindScroll() {
      let ticking = false;
      const update = () => {
        this.classList.toggle('is-scrolled', window.scrollY > 24);
        ticking = false;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      update();
    }

    /* ---------- bag count: hidden when empty ---------- */

    bindCount() {
      const sync = (el) => {
        const n = parseInt((el.textContent || '').replace(/[^0-9]/g, ''), 10);
        const text = (el.textContent || '').trim();
        el.hidden = !(text === '~' || (Number.isFinite(n) && n > 0));
      };
      const counts = [...this.querySelectorAll('.cart-count')];
      counts.forEach(sync);
      const mo = new MutationObserver((list) => list.forEach((m) => {
        const el = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        const host = el && el.closest && el.closest('.cart-count');
        if (host) sync(host);
      }));
      counts.forEach((c) => mo.observe(c, { childList: true, characterData: true, subtree: true }));
    }

    /* ---------- mobile menu ---------- */

    bindMobile() {
      if (!this.menu || !this.menuBtn) return;
      this.menuBtn.addEventListener('click', () => this.openMenu());
      this.menu.querySelector('[data-menu-close]')?.addEventListener('click', () => this.closeMenu());
      this.menu.querySelector('[data-menu-search]')?.addEventListener('click', () => this.closeMenu(true));
      this.menu.querySelectorAll('.inf-mobile__expand').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = this.menu.querySelector('#' + btn.getAttribute('aria-controls'));
          const open = btn.getAttribute('aria-expanded') !== 'true';
          btn.setAttribute('aria-expanded', String(open));
          if (sub) sub.hidden = !open;
        });
      });
      this.menu.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); this.closeMenu(); return; }
        if (e.key !== 'Tab') return;
        const f = [...this.menu.querySelectorAll('a[href], button')].filter((el) => el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    openMenu() {
      this.menu.hidden = false;
      this.menuBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('inf-menu-open');
      requestAnimationFrame(() => {
        this.menu.classList.add('is-open');
        this.menu.setAttribute('tabindex', '-1');
        this.menu.focus({ preventScroll: true });   // focus the dialog, not Close: no focus box on touch
      });
    }

    closeMenu(skipFocus) {
      if (!this.menu || this.menu.hidden) return;
      this.menu.classList.remove('is-open');
      this.menuBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('inf-menu-open');
      const done = () => { this.menu.hidden = true; };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 240);
      if (!skipFocus) this.menuBtn.focus();
    }
  }

  customElements.define('infinote-header', InfinoteHeader);

  // Phone/tablet bag icon: the theme's own click binding does not open the cart drawer from the compact header,
  // so open/close it directly. Capture phase + stopImmediatePropagation keeps the theme handler from double-toggling.
  document.addEventListener('click', (e) => {
    const bag = e.target.closest && e.target.closest('.inf-bag-compact');
    if (!bag) return;
    const cart = document.querySelector('cart-notification');
    const wrap = document.querySelector('.minicart__wrapper');
    if (!cart || !wrap || typeof cart.open !== 'function') return; // fall back to the cart page link
    e.preventDefault();
    e.stopImmediatePropagation();
    if (wrap.classList.contains('open')) cart.close(); else cart.open();
  }, true);
})();
